import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createClient } from '@connectrpc/connect'
import { createConnectQueryKey, useTransport } from '@connectrpc/connect-query'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useStore } from '@tanstack/react-store'
import { createRealtimeClient } from '@neon/realtime/client'
import { RealtimeProvider, useLiveQuery } from '@neon/realtime-react'
import { toast } from 'sonner'
import { CortadoService } from '@/gen/cortado/v1/cortado_pb'
import type { RealtimeQueryEnum } from '@/gen/cortado/v1/realtime_pb'
import { isDemoModeActive } from '@/lib/demo-mode'
import {
  LIVE_FEEDS,
  MAX_TOASTS_PER_UPDATE,
  createNewRowDetector,
  toSealedQuery,
  type LiveFeed,
  type LiveToast,
  type SealedQuery,
} from '@/lib/live-activity'
import { organizationStore } from '@/stores/organization.store'

interface LiveSession {
  generation: number
  url: string
  queries: { query: RealtimeQueryEnum; sealed: SealedQuery }[]
}

/**
 * Mounts Neon Realtime live queries for the selected organization and turns
 * new rows into toasts plus cache invalidation. Renders nothing. A no-op when
 * the backend reports Realtime disabled, in demo mode, or on any error: live
 * updates are an enhancement and must never break the app.
 */
export function LiveActivity() {
  const organizationId = useStore(organizationStore, (s) => s.selectedOrganizationId)
  if (!organizationId || isDemoModeActive()) return null
  // Remount per org so every subscription and detector starts clean.
  return <LiveActivityForOrg key={organizationId} organizationId={organizationId} />
}

/**
 * Waits before each fresh-session attempt after a failure: a failed fetch, or
 * a watcher whose subscription died (e.g. renewal exhausted). Once these run
 * out, live updates stay off until the org changes or the page reloads. A
 * session that goes live resets the budget.
 */
export const LIVE_RETRY_DELAYS_MS = [5_000, 15_000, 60_000] as const

function LiveActivityForOrg({ organizationId }: { organizationId: string }) {
  const transport = useTransport()
  const client = useMemo(() => createClient(CortadoService, transport), [transport])
  const [session, setSession] = useState<LiveSession | null>(null)
  // Bumping this fetches a fresh session, which remounts the provider and
  // every watcher (detector baselines restart with it).
  const [generation, setGeneration] = useState(0)
  const failures = useRef(0)
  const retryTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  const scheduleRetry = useCallback(() => {
    if (retryTimer.current !== undefined) return
    const delay = LIVE_RETRY_DELAYS_MS[failures.current]
    if (delay === undefined) return
    failures.current += 1
    retryTimer.current = setTimeout(() => {
      retryTimer.current = undefined
      setGeneration((g) => g + 1)
    }, delay)
  }, [])
  useEffect(() => () => clearTimeout(retryTimer.current), [])

  const onLive = useCallback(() => {
    failures.current = 0
  }, [])
  const onError = useCallback(() => {
    setSession(null)
    scheduleRetry()
  }, [scheduleRetry])

  useEffect(() => {
    let cancelled = false
    // Sealed queries are bearer credentials: kept in component state only,
    // never in the TanStack Query cache or browser storage.
    client
      .getRealtimeQueries({ organizationId })
      .then((resp) => {
        if (cancelled || !resp.enabled || !resp.url) return
        setSession({
          generation,
          url: resp.url,
          queries: resp.queries
            .filter((q) => q.sealed && LIVE_FEEDS.has(q.query))
            .map((q) => ({ query: q.query, sealed: toSealedQuery(q.sealed!) })),
        })
      })
      .catch(() => {
        // Realtime is optional; normal fetching keeps the UI correct.
        if (!cancelled) scheduleRetry()
      })
    return () => {
      cancelled = true
    }
  }, [client, organizationId, generation, scheduleRetry])

  const realtimeClient = useMemo(() => (session ? createRealtimeClient({ url: session.url }) : null), [session])
  useEffect(() => () => realtimeClient?.close(), [realtimeClient])

  if (!session || !realtimeClient || session.queries.length === 0) return null

  return (
    <RealtimeProvider key={session.generation} client={realtimeClient}>
      {session.queries.map((q) => (
        <LiveFeedWatcher
          key={q.query}
          feed={LIVE_FEEDS.get(q.query)!}
          initial={q.sealed}
          onLive={onLive}
          onError={onError}
          refresh={async () => {
            const resp = await client.getRealtimeQueries({ organizationId, queries: [q.query] })
            const next = resp.queries.find((r) => r.query === q.query)?.sealed
            if (!resp.enabled || !next) throw new Error('realtime query no longer available')
            return toSealedQuery(next)
          }}
        />
      ))}
    </RealtimeProvider>
  )
}

export function LiveFeedWatcher<Row>({
  feed,
  initial,
  refresh,
  onLive,
  onError,
}: {
  feed: LiveFeed<Row>
  initial: SealedQuery
  refresh: () => Promise<SealedQuery>
  /** The subscription is live. */
  onLive?: () => void
  /** The subscription died for good (e.g. renewal exhausted). */
  onError?: () => void
}) {
  const queryClient = useQueryClient()
  const transport = useTransport()
  const navigate = useNavigate()
  // The parent recreates `refresh` each render; keep the SDK's callback stable.
  const refreshRef = useRef(refresh)
  refreshRef.current = refresh
  const refreshQuery = useCallback(() => refreshRef.current(), [])
  const { data, status } = useLiveQuery<Row>(initial, { refreshQuery })
  const detect = useRef(createNewRowDetector<Row>({ getId: feed.getId, getTime: feed.getTime }))

  useEffect(() => {
    if (status === 'live') onLive?.()
    else if (status === 'error') onError?.()
  }, [status, onLive, onError])

  useEffect(() => {
    if (status !== 'live' || !data) return
    const fresh = detect.current(data)
    if (fresh.length === 0) return

    for (const schema of feed.invalidates) {
      void queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({ schema, transport, cardinality: undefined }),
      })
    }
    const show = (id: string, t: LiveToast) =>
      toast.success(t.title, {
        id,
        description: t.description,
        action: t.href ? { label: 'View', onClick: () => void navigate({ to: t.href! }) } : undefined,
      })
    if (fresh.length > MAX_TOASTS_PER_UPDATE) {
      show(`live:${feed.query}:summary:${feed.getId(fresh[0])}`, feed.summary(fresh.length))
    } else {
      for (const row of fresh) show(`live:${feed.query}:${feed.getId(row)}`, feed.toast(row))
    }
  }, [data, status, feed, queryClient, transport, navigate])

  return null
}
